import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Encounter } from 'app/core/interfaces/encounter';
import { Healthcareservice } from 'app/core/interfaces/healthcareservice';
import { OrganizationInterface } from 'app/core/interfaces/organization';
import { EncounterService } from 'app/core/http/services/encounter.service';
import { OrganizationLocationService } from 'app/core/http/services/organization-location.service';
import { UtilsService } from '../../utils.service';
import { forkJoin, finalize, of, Subject, take, takeUntil, timeout } from 'rxjs';
import { Location as LocationModel } from 'app/core/interfaces/location';

import dayjs from 'app/core/utils/dayjs-config';

@Component({
  selector: 'schedule-organization-hour',
  templateUrl: './schedule-organization-hour.component.html',
  styleUrls: ['./schedule-organization-hour.component.scss']
})
export class ScheduleOrganizationHourComponent implements OnChanges, OnDestroy {

  currentDate = dayjs().toDate();
  selectedSlot: { date: string; hour: string } | null = null;

  organizationHours: any[] = [];
  availability: Record<string, string[]> = {};
  dates: { day: string; date: string; fullDate: string }[] = [];
  public expanded: boolean = false;
  animationDirection: 'left' | 'right' | null = null;
  public offset = 0;

  public encountersDone: any = {};
  isAnimating = false;

  public blockedDates: any[] = []

  @Input() organization?: OrganizationInterface;
  @Input() healthcareservice?: Healthcareservice

  @Input() organization_id: string = '';
  @Input() location?: LocationModel;


  public isByOrder = false
  @Output() onSelectedDatetime = new EventEmitter<any>();

  public averageDuration: number = 30
  public hasAvailableTime: boolean = false
  public loading = false;
  public hoursLoadMessage: string | null = null;
  public canRetryHoursLoad = false;

  private readonly destroy$ = new Subject<void>();
  private readonly reload$ = new Subject<void>();
  private loadSequence = 0;

  constructor(
    private encounterService: EncounterService,
    private organizationLocationService: OrganizationLocationService,
    private utilsService: UtilsService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['organization'] || changes['organization_id'] || changes['healthcareservice'] || changes['location']) {
      // ngOnChanges runs before ngOnInit. Build the date columns before mapping opening hours.
      this.generateWeek(this.currentDate);
      this.loadCalendarData();
    }
  }

  ngOnDestroy(): void {
    this.reload$.next();
    this.reload$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private isPreviousWeekAllowed(): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const proposedDate = new Date(this.currentDate);
    proposedDate.setDate(proposedDate.getDate() - this.getLimitDays());
    proposedDate.setHours(0, 0, 0, 0);

    return proposedDate >= today;
  }

  get canGoToPreviousWeek(): boolean {
    return this.isPreviousWeekAllowed();
  }

  get firstAvailableLabel(): string | null {
    for (const date of this.dates) {
      const slots = this.availability[date.fullDate] ?? [];
      for (const slot of slots) {
        let availableHour: string | null = null;
        if (this.isByOrder) {
          const [start, end] = slot.split(' - ');
          if (start && end) availableHour = this.getFirstAvailableSlot(date.fullDate, start, end);
        } else if (this.isAvailable(date.fullDate, slot)) {
          availableHour = slot;
        }

        if (availableHour) return `${date.day}, ${date.date} às ${availableHour}`;
      }
    }

    return null;
  }

  goToPreviousWeek() {
    if (this.isAnimating || !this.isPreviousWeekAllowed()) return;

    this.animationDirection = 'left';
    this.isAnimating = true;

    this.previousWeek();

    setTimeout(() => {
      this.offset -= this.getLimitDays();
      this.animationDirection = null;
      this.isAnimating = false;
    }, 300);
  }

  goToNextWeek() {
    if (this.isAnimating) return;
    this.animationDirection = 'right';
    this.isAnimating = true;

    this.nextWeek();

    setTimeout(() => {
      this.offset += this.getLimitDays();
      this.animationDirection = null;
      this.isAnimating = false;
    }, 300);
  }

  toggleExpandAll(): void {
    this.expanded = !this.expanded;
  }

  getLimitDays() {
    return this.utilsService.getLimitDays();
  }

  hasMoreThanFiveSlots(): boolean {
    return Object.values(this.availability).some(slots => slots.length > 5);
  }

  loadCalendarData(): void {
    this.reload$.next();
    const requestId = ++this.loadSequence;
    const organizationId = this.organization?.organization_id || this.organization_id;

    this.loading = true;
    this.hoursLoadMessage = null;
    this.canRetryHoursLoad = false;
    this.organizationHours = [];
    this.encountersDone = {};
    this.availability = {};
    this.hasAvailableTime = false;

    if (!organizationId || !this.healthcareservice?.healthcareservice_id) {
      this.hoursLoadMessage = 'Não foi possível identificar a agenda desta clínica para o procedimento.';
      this.loading = false;
      return;
    }

    const locationRequest = this.location && Array.isArray(this.location.hoursOfOperation)
      ? of(this.location)
      : this.organizationLocationService.showByOrganization(organizationId);

    forkJoin({
      location: locationRequest,
      encounters: this.encounterService.listByOrganizationNew(organizationId),
    })
      .pipe(
        timeout({ first: 15000 }),
        take(1),
        takeUntil(this.reload$),
        takeUntil(this.destroy$),
        finalize(() => {
          if (requestId === this.loadSequence) this.loading = false;
        }),
      )
      .subscribe({
        next: ({ location, encounters }) => {
          if (requestId !== this.loadSequence) return;

          this.organizationHours = Array.isArray(location?.hoursOfOperation) ? location.hoursOfOperation : [];
          this.encountersDone = this.groupEncountersByDate(encounters);
          this.averageDuration = this.getAverageDuration();
          this.blockedDates = (this.organization?.modifiedDays ?? [])
            .filter((day) => day?.date)
            .map((day) => dayjs(day.date).tz('America/Sao_Paulo').format('YYYY-MM-DD'));
          this.mapAvailabilityFromOrganizationHours();
        },
        error: (error) => {
          if (requestId !== this.loadSequence) return;
          console.error('Não foi possível carregar os horários da clínica:', error);
          this.hoursLoadMessage = error?.name === 'TimeoutError'
            ? 'A busca de horários demorou mais de 15 segundos. Verifique sua conexão e tente novamente.'
            : 'Não foi possível carregar os horários agora. Verifique sua conexão e tente novamente.';
          this.canRetryHoursLoad = true;
        },
      });
  }

  private getAverageDuration(): number {
    const interval = Number(this.healthcareservice?.averageDuration);
    return Number.isFinite(interval) && interval > 0 ? interval : 30;
  }

  private groupEncountersByDate(encounters: Encounter[] | null | undefined): Record<string, string[]> {
    return (Array.isArray(encounters) ? encounters : []).reduce<Record<string, string[]>>((grouped, encounter) => {
      const [date, rawTime] = String(encounter?.startDate ?? '').replace('T', ' ').split(' ');
      const time = rawTime?.slice(0, 5);
      if (!date || !time || String(encounter?.status).toLowerCase() === 'cancelled') return grouped;

      grouped[date] ??= [];
      grouped[date].push(time);
      return grouped;
    }, {});
  }


  generateWeek(baseDate: Date) {
    const dias = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    this.dates = [];
    let limit = this.getLimitDays();

    for (let i = 0; i < limit; i++) {
      const d = new Date(baseDate);
      d.setDate(baseDate.getDate() + i);

      const day = dias[d.getDay()];
      const date = d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
      const fullDate = [
        d.getFullYear(),
        String(d.getMonth() + 1).padStart(2, '0'),
        String(d.getDate()).padStart(2, '0'),
      ].join('-');

      this.dates.push({ day, date, fullDate });
    }
  }



  mapAvailabilityFromOrganizationHours(): void {
    this.availability = {};
    this.hasAvailableTime = false;
    const blockedDates = new Set(this.blockedDates ?? []);

    for (const item of Array.isArray(this.organizationHours) ? this.organizationHours : []) {
      const dayOfWeek = Number(item?.dayOfWeek);
      const timeStart = item?.timeStart;
      const timeEnd = item?.timeEnd;
      if (!Number.isInteger(dayOfWeek) || dayOfWeek < 0 || dayOfWeek > 6 || !timeStart || !timeEnd) continue;

      const matchingDates = this.dates.filter((date) => this.getDayOfWeek(date.fullDate) === dayOfWeek);

      for (const date of matchingDates) {
        const fullDate = date.fullDate;
        if (blockedDates.has(fullDate)) continue;

        if (!this.availability[fullDate]) this.availability[fullDate] = [];

        if (this.isByOrder) {
          this.availability[fullDate].push(`${timeStart} - ${timeEnd}`);
        } else {
          const availableSlots = this.generateTimeSlots(timeStart, timeEnd).filter(
            (slot) => !this.encountersDone?.[fullDate]?.includes(slot) && this.isDateHourFuture(fullDate, slot)
          );
          this.availability[fullDate].push(...availableSlots);
        }

        this.availability[fullDate] = Array.from(new Set(this.availability[fullDate])).sort();
      }
    }

    this.hasAvailableTime = Object.entries(this.availability).some(([date, slots]) =>
      slots.some((slot) => this.isByOrder ? this.isAvailableByOrder(date, slot) : this.isAvailable(date, slot))
    );
  }



  generateTimeSlots(start: string, end: string, interval: number = this.averageDuration): string[] {
    const slots: string[] = [];
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(start) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(end) || interval <= 0) {
      return slots;
    }

    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);

    const startDate = new Date();
    startDate.setHours(startH, startM, 0, 0);

    const endDate = new Date();
    endDate.setHours(endH, endM, 0, 0);


    while (startDate < endDate) {
      const hh = String(startDate.getHours()).padStart(2, '0');
      const mm = String(startDate.getMinutes()).padStart(2, '0');
      slots.push(`${hh}:${mm}`);
      startDate.setMinutes(startDate.getMinutes() + interval);
    }

    return slots;
  }

  isAvailable(date: string, hour: string): boolean {
    const isSlotAvailable = this.availability[date]?.includes(hour);
    const isSlotTaken = this.encountersDone?.[date]?.includes(hour);

    if (!isSlotAvailable || isSlotTaken) return false;

    return this.isDateHourFuture(date, hour);
  }


  isAvailableByOrder(date: string, range: string): boolean {
    const [start, end] = range.split(' - ');
    if (!start || !end || !this.isDateHourFuture(date, end)) return false;
    return this.getFirstAvailableSlot(date, start, end) !== null;
  }

  isSelected(date: string, hour: string): boolean {
    return this.selectedSlot?.date === date && this.selectedSlot?.hour === hour;
  }

  isSelectedByOrder(date: string, range: string): boolean {
    const [start, end] = range.split(' - ');
    const firstAvailableSlot = this.getFirstAvailableSlot(date, start, end);
    return firstAvailableSlot !== null && this.isSelected(date, firstAvailableSlot);
  }

  selectHour(date: string, hour: string): void {
    this.selectedSlot = { date, hour };
    const datetime = `${date} ${hour}`;

    this.onSelectedDatetime.emit(datetime);
  }

  selectHourByOrder(date: string, range: string) {
    const [start, end] = range.split(' - ');
    const selectedHour = this.getFirstAvailableSlot(date, start, end);
    if (!selectedHour) return;

    this.selectedSlot = { date, hour: selectedHour };
    this.onSelectedDatetime.emit(`${date} ${selectedHour}`);
  }


  isDateHourFuture(date: string, hour: string) {
    const [hourPart, minutePart] = hour.split(':').map(Number);

    const [year, month, day] = date.split('-').map(Number);
    const slotDateTime = new Date(year, month - 1, day, hourPart, minutePart, 0);

    const now = new Date();

    return slotDateTime > now;
  }

  getFirstAvailableSlot(date: string, start: string, end: string): string | null {
    const slots = this.generateTimeSlots(start, end);
    const encountersDoneForDate = this.encountersDone[date] || [];

    const availableSlot = slots.find((slot) => !encountersDoneForDate.includes(slot) && this.isDateHourFuture(date, slot));

    return availableSlot || null;
  }

  private getDayOfWeek(fullDate: string): number {
    const [year, month, day] = fullDate.split('-').map(Number);
    return new Date(year, month - 1, day).getDay();
  }

  previousWeek() {
    this.currentDate.setDate(this.currentDate.getDate() - this.getLimitDays());
    this.generateWeek(this.currentDate);
    this.mapAvailabilityFromOrganizationHours();
  }

  nextWeek() {
    this.currentDate.setDate(this.currentDate.getDate() + this.getLimitDays());
    this.generateWeek(this.currentDate);
    this.mapAvailabilityFromOrganizationHours();
  }

  public toggleHorarios(index: number) {
    this.utilsService.toggleHorarios(index);
  }


}
